/**
 * ChoiceChips — clickable options the Companion offered (parsed from a
 * `tonnie-choices` block). Clicking a chip sends it as the next turn; "Other…"
 * focuses the composer so the user can type their own — the AskUserQuestion
 * pattern, in the conversation.
 */

import { useTranslation } from '@tonnie/locale';
import { cn } from '@tonnie/ui';

interface IChoiceChipsProps {
  options: string[];
  onPick: (option: string) => void;
  disabled?: boolean;
}

export function ChoiceChips({ options, onPick, disabled }: IChoiceChipsProps) {
  const { t } = useTranslation();
  if (options.length === 0) return null;

  const focusComposer = () => {
    const el = document.querySelector<HTMLInputElement>('[data-testid="copilot-message-input"]');
    el?.focus();
  };

  return (
    <div
      className="mt-2 flex flex-wrap gap-1.5"
      role="group"
      aria-label={t('copilot.choices.label')}
    >
      {options.map((option, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onPick(option)}
          disabled={disabled}
          className={cn(
            'rounded-full border border-border bg-background px-3 py-1 text-xs text-left',
            'transition-colors hover:bg-secondary hover:border-primary/40 disabled:opacity-50'
          )}
        >
          {option}
        </button>
      ))}
      <button
        type="button"
        onClick={focusComposer}
        disabled={disabled}
        className={cn(
          'rounded-full border border-dashed border-border px-3 py-1 text-xs text-muted-foreground',
          'transition-colors hover:bg-secondary disabled:opacity-50'
        )}
      >
        {t('copilot.choices.other')}
      </button>
    </div>
  );
}
